import { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, Send } from 'lucide-react';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { useFilters } from '@/hooks/useFilters';
import { usePagination } from '@/hooks/usePagination';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { FilterBar } from '@/components/common/FilterBar';
import { TicketStatusBadge, PriorityBadge } from '@/components/common/StatusBadge';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dropdown, DropdownItem } from '@/components/ui/dropdown';
import { TablePagination } from '@/components/ui/pagination';
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/modal';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  useTickets,
  useReplyToTicket,
  useUpdateTicketStatus,
} from '../hooks/useSupport';
import type { SupportTicketAPI } from '../api/support.api';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions = [
  { value: 'all', label: 'All Priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  { value: 'booking', label: 'Booking' },
  { value: 'payment', label: 'Payment' },
  { value: 'driver', label: 'Driver' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

export function SupportTicketsPage() {
  const { page, limit, setPage, setLimit } = usePagination();
  const { filters, setFilter, resetFilters } = useFilters({
    defaultValues: {
      search: '',
      status: 'all',
      priority: 'all',
      category: 'all',
    },
  });

  const [selectedTicket, setSelectedTicket] = useState<SupportTicketAPI | null>(null);
  const [replyText, setReplyText] = useState('');

  const queryParams = {
    page,
    limit,
    ...(filters.search ? { search: filters.search as string } : {}),
    ...(filters.status !== 'all' ? { status: filters.status as string } : {}),
    ...(filters.priority !== 'all' ? { priority: filters.priority as string } : {}),
    ...(filters.category !== 'all' ? { category: filters.category as string } : {}),
  };

  const { data, isLoading } = useTickets(queryParams);
  const { mutate: sendReply, isPending: isSending } = useReplyToTicket();
  const { mutate: updateStatus } = useUpdateTicketStatus();

  const tickets = data?.data || [];
  const pagination = data?.pagination;

  const handleSendReply = () => {
    if (!replyText.trim() || !selectedTicket) return;
    sendReply(
      { id: selectedTicket.id, content: replyText.trim() },
      {
        onSuccess: () => {
          setReplyText('');
          // Refresh modal ticket data
          setSelectedTicket(null);
        },
      },
    );
  };

  const handleStatusChange = (ticketId: string, status: string) => {
    updateStatus({ id: ticketId, status });
    if (selectedTicket?.id === ticketId) {
      setSelectedTicket((prev) => prev ? { ...prev, status } : prev);
    }
  };

  const columns: ColumnDef<SupportTicketAPI>[] = [
    {
      accessorKey: 'ticket_number',
      header: 'Ticket',
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.ticket_number}</span>
      ),
    },
    {
      accessorKey: 'subject',
      header: 'Subject',
      cell: ({ row }) => (
        <div className="max-w-[250px]">
          <p className="font-medium truncate">{row.original.subject}</p>
          <Badge variant="outline" className="capitalize text-xs mt-1">
            {row.original.category}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar alt={row.original.customer?.name} size="xs" />
          <div>
            <p className="font-medium text-sm">{row.original.customer?.name}</p>
            <p className="text-xs text-muted-foreground">{row.original.customer?.email}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <PriorityBadge priority={row.original.priority as any} />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <TicketStatusBadge status={row.original.status as any} />
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm">
          <p>{formatDate(row.original.created_at)}</p>
          <p className="text-xs text-muted-foreground">
            {formatRelativeTime(row.original.created_at)}
          </p>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          }
        >
          <DropdownItem
            icon={<Eye className="h-4 w-4" />}
            onClick={() => setSelectedTicket(row.original)}
          >
            View & Reply
          </DropdownItem>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Tickets"
        description="Manage customer support requests and complaints"
      />

      <FilterBar
        filters={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search by ticket ID, subject, or customer...',
            className: 'w-80',
          },
          {
            type: 'select',
            key: 'status',
            placeholder: 'Status',
            options: statusOptions,
          },
          {
            type: 'select',
            key: 'priority',
            placeholder: 'Priority',
            options: priorityOptions,
          },
          {
            type: 'select',
            key: 'category',
            placeholder: 'Category',
            options: categoryOptions,
          },
        ]}
        values={filters}
        onChange={(key, value) => setFilter(key as keyof typeof filters, value as string)}
        onReset={resetFilters}
      />

      <DataTable
        columns={columns}
        data={tickets}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) => setSelectedTicket(row)}
      />

      <TablePagination
        currentPage={pagination?.page || page}
        totalPages={pagination?.totalPages || 1}
        totalItems={pagination?.total || 0}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
      />

      {/* Ticket Detail Modal */}
      <Modal
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        className="max-w-2xl"
      >
        {selectedTicket && (
          <>
            <ModalHeader onClose={() => setSelectedTicket(null)}>
              <ModalTitle>
                {selectedTicket.ticket_number} - {selectedTicket.subject}
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <TicketStatusBadge status={selectedTicket.status as any} />
                    <PriorityBadge priority={selectedTicket.priority as any} />
                  </div>
                  <Select
                    value={selectedTicket.status}
                    onChange={(value) => handleStatusChange(selectedTicket.id, value)}
                    options={statusOptions.filter((o) => o.value !== 'all')}
                    className="w-40"
                  />
                </div>

                <div className="border rounded-lg p-4 bg-muted/50">
                  <p className="text-sm font-medium">Customer: {selectedTicket.customer?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedTicket.customer?.email}</p>
                  {selectedTicket.customer?.phone && (
                    <p className="text-sm text-muted-foreground">{selectedTicket.customer.phone}</p>
                  )}
                </div>

                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {(selectedTicket.messages || []).map((message) => (
                    <div
                      key={message.id}
                      className={`p-3 rounded-lg ${
                        message.sender_type === 'admin'
                          ? 'bg-primary/10 ml-8'
                          : 'bg-muted mr-8'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{message.sender?.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(message.created_at)}
                        </span>
                      </div>
                      <p className="text-sm">{message.content}</p>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="flex-1"
                  />
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                Close
              </Button>
              <Button
                onClick={handleSendReply}
                disabled={!replyText.trim() || isSending}
              >
                {isSending ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Reply
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
}
